import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";

import {
  getUserBallotsForVote,
  getVoteById,
} from "../../../services/voteServices";
import { FaCheckCircle, FaSpinner, FaTimes } from "react-icons/fa";

function QuestionText({ value }) {
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <ReactQuill value={value || ""} readOnly theme="bubble" />
    </div>
  );
}

export default function UserBallotAnswersModal({
  isOpen,
  onClose,
  voteId,
  userId,
  token,
}) {
  const [vote, setVote] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !voteId || !userId) return;

    let mounted = true;

    async function loadAnswers() {
      try {
        setLoading(true);

        const [voteData, answerRows] = await Promise.all([
          getVoteById(voteId),
          getUserBallotsForVote(userId, voteId, token),
        ]);

        if (!mounted) return;

        setVote(voteData);
        setRows(Array.isArray(answerRows) ? answerRows : []);
      } catch (error) {
        console.error("Impossible de charger vos réponses :", error);
        toast.error("Impossible de charger vos réponses.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadAnswers();

    return () => {
      mounted = false;
      setVote(null);
      setRows([]);
      setLoading(false);
    };
  }, [isOpen, token, userId, voteId]);

  const answersByQuestion = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const questionId = row.question_id;

      if (!map.has(questionId)) {
        map.set(questionId, {
          openText: null,
          choices: [],
        });
      }

      const answer = map.get(questionId);

      if (row.answer_text) {
        answer.openText = row.answer_text;
      }

      if (row.choice_id) {
        answer.choices.push(row.choice_id);
      }
    });

    return map;
  }, [rows]);

  const view = useMemo(() => {
    if (!vote) return [];

    return (vote.questions || []).map((question) => {
      const answer = answersByQuestion.get(question.id) || {
        openText: null,
        choices: [],
      };

      if (question.type === "open") {
        return {
          question,
          type: "open",
          value: answer.openText || "—",
        };
      }

      if (question.type === "single") {
        const selectedChoice = (question.choices || []).find(
          (choice) => choice.id === (answer.choices[0] ?? null)
        );

        return {
          question,
          type: "single",
          value: selectedChoice?.choice_text || "—",
        };
      }

      if (question.type === "multiple") {
        const labels = answer.choices
          .map(
            (choiceId) =>
              (question.choices || []).find((choice) => choice.id === choiceId)
                ?.choice_text
          )
          .filter(Boolean);

        return {
          question,
          type: "multiple",
          value: labels,
        };
      }

      return {
        question,
        type: "unknown",
        value: "—",
      };
    });
  }, [answersByQuestion, vote]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-[101] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-950 sm:max-w-3xl sm:rounded-[2rem]">
        <div className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white sm:px-7">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-emerald-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-sky-200">
                <FaCheckCircle />
                Mes réponses
              </p>

              <h3 className="mt-2 text-xl font-bold sm:text-2xl">
                {vote?.title || "Réponses enregistrées"}
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-200">
                Consulte les réponses déjà transmises pour ce vote ou sondage.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading || !vote ? (
            <div className="flex min-h-40 items-center justify-center rounded-[1.5rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/70">
              <div className="flex items-center gap-3 text-sm font-bold text-slate-500 dark:text-slate-300">
                <FaSpinner className="animate-spin" />
                Chargement des réponses...
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {view.map(({ question, type, value }, index) => (
                <div
                  key={question.id}
                  className="rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-900/60"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-bold text-white dark:bg-white dark:text-slate-950">
                      Question {index + 1}
                    </span>
                  </div>

                  <QuestionText value={question.question_text} />

                  {type === "open" && (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3 text-sm leading-6 text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                      {value}
                    </div>
                  )}

                  {type === "single" && (
                    <div className="mt-3 inline-flex rounded-full bg-sky-100 px-3 py-1.5 text-xs font-bold text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                      {value}
                    </div>
                  )}

                  {type === "multiple" && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Array.isArray(value) && value.length > 0 ? (
                        value.map((label, labelIndex) => (
                          <span
                            key={`${question.id}-${labelIndex}`}
                            className="inline-flex rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                          >
                            {label}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          —
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:p-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:shadow-xl dark:bg-white dark:text-slate-950 sm:w-auto"
            >
              <FaTimes />
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}